import React from "react";
import { ChevronDown } from "lucide-react";
import { DiamondNetworkMark } from "./Logo";

const HERO_IMG =
  "https://images.pexels.com/photos/18356308/pexels-photo-18356308.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920";

const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-[#0A1C33]"
    >
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center scale-105"
        style={{
          backgroundImage: `url(${HERO_IMG})`,
          filter: "saturate(1.05) contrast(1.05)",
        }}
      />
      {/* Cinematic overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A1C33] via-[#0A1C33]/70 to-[#0A1C33]/30" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A1C33]/70 via-transparent to-transparent" />

      {/* Grain */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.18] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />

      {/* Corner brand marker */}
      <div className="hidden lg:flex absolute top-28 left-6 md:left-10 items-center opacity-95 pointer-events-none">
        <img
          src="https://customer-assets.emergentagent.com/job_vision-landing-7/artifacts/qhd5k6at_3472EA0A-A761-4F6C-9324-77B0D49AF8BB.png"
          alt="KIDH — Real Estate"
          className="h-14 w-auto object-contain brightness-0 invert"
        />
      </div>

      {/* Right side vertical rule */}
      <div className="hidden md:flex absolute right-10 top-1/2 -translate-y-1/2 flex-col items-center gap-6 text-white/60">
        <span className="rotate-90 origin-center whitespace-nowrap font-body text-[0.65rem] uppercase tracking-[0.36em]">
          EKIP Holding · 2026
        </span>
        <div className="w-px h-24 bg-white/30" />
        <DiamondNetworkMark size={20} tone="white" />
      </div>

      {/* Main content */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-16 pt-32 pb-24 w-full">
        <div className="max-w-4xl">
          <div
            className="inline-flex items-center gap-3 px-4 py-2 border border-white/25 mb-8 backdrop-blur-sm animate-fade-up"
            style={{ animationDelay: "0.1s" }}
          >
            <span className="kidh-diamond" style={{ background: "#E8611A" }} />
            <span className="font-body text-white/90 text-[0.7rem] uppercase tracking-[0.28em]">
              Vision 2035 · Côte d'Ivoire
            </span>
          </div>

          <h1
            className="font-heading font-black text-white leading-[0.95] tracking-tighter text-5xl sm:text-6xl md:text-7xl lg:text-[6.5rem] animate-fade-up"
            style={{ animationDelay: "0.25s" }}
            data-testid="hero-title"
          >
            Plateforme
            <br />
            <span className="text-[#E8611A]">Urbaine</span> Intégrée<span className="text-[#E8611A]">.</span>
          </h1>

          <p
            className="mt-8 max-w-2xl font-body text-white/85 text-base md:text-xl leading-relaxed animate-fade-up"
            style={{ animationDelay: "0.45s" }}
            data-testid="hero-subtitle"
          >
            Nous ne construisons pas des immeubles. Nous concevons des quartiers où
            l'école, la clinique, la station, le marché et l'espace de travail
            sont à moins de <span className="text-[#E8611A] font-semibold">10 minutes à pied</span>.
          </p>

          <div
            className="mt-12 flex flex-col sm:flex-row gap-4 animate-fade-up"
            style={{ animationDelay: "0.65s" }}
          >
            <a href="#poles" className="kidh-btn-primary" data-testid="hero-cta-primary">
              Découvrir nos quartiers
              <span className="kidh-diamond" style={{ background: "#FAFAF8" }} />
            </a>
            <a href="#contact" className="kidh-btn-outline" data-testid="hero-cta-secondary">
              Investir avec KIDH
            </a>
          </div>

          {/* Compact KPI strip */}
          <div
            className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-y-8 gap-x-10 max-w-3xl border-t border-white/15 pt-10 animate-fade-up"
            style={{ animationDelay: "0.85s" }}
          >
            {[
              { v: "60 000", l: "Logements" },
              { v: "8", l: "Pôles urbains" },
              { v: "300 000", l: "Personnes logées" },
              { v: "47 000", l: "Emplois créés" },
            ].map((k) => (
              <div key={k.l}>
                <div className="font-heading font-black text-white text-2xl md:text-3xl tracking-tighter">
                  {k.v}
                </div>
                <div className="font-body text-white/60 text-[0.65rem] uppercase tracking-[0.24em] mt-1">
                  {k.l}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <a
        href="#philosophie"
        aria-label="Défiler"
        data-testid="hero-scroll-indicator"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/70 hover:text-white transition-colors"
      >
        <span className="font-body text-[0.6rem] uppercase tracking-[0.32em]">Scroll</span>
        <ChevronDown size={22} className="animate-chevron-bounce" />
      </a>
    </section>
  );
};

export default Hero;
